import { CheckCircle2, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { cn } from '../lib/cn';
import { useAdminHealth, formatRelativeTime } from '../api/hooks';
import { useQueryClient } from '@tanstack/react-query';
import type { AdminComponentHealth } from '../api/types';

type HealthStatus = 'healthy' | 'degraded' | 'unhealthy';

function getStatusIcon(status: HealthStatus) {
  switch (status) {
    case 'healthy':
      return <CheckCircle2 size={20} className="text-profit" />;
    case 'degraded':
      return <AlertTriangle size={20} className="text-status-pending" />;
    case 'unhealthy':
      return <XCircle size={20} className="text-loss" />;
  }
}

function getStatusBadge(status: HealthStatus) {
  return (
    <span
      className={cn(
        'badge',
        status === 'healthy' && 'badge-active',
        status === 'degraded' && 'badge-pending',
        status === 'unhealthy' && 'badge-suspended'
      )}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function ServiceCardSkeleton() {
  return (
    <Card padding="md" hover={false}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <Skeleton className="w-5 h-5 rounded-full" />
          <Skeleton className="h-5 w-32" />
        </div>
        <Skeleton className="h-5 w-16" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Skeleton className="h-3 w-12 mb-1" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div>
          <Skeleton className="h-3 w-20 mb-1" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
    </Card>
  );
}

function ServiceCard({ service }: { service: AdminComponentHealth }) {
  return (
    <Card padding="md" hover={false}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {getStatusIcon(service.status)}
          <h3 className="text-card-title text-text capitalize">
            {service.name}
          </h3>
        </div>
        {getStatusBadge(service.status)}
      </div>

      <div className="grid grid-cols-2 gap-4 text-body-sm">
        <div>
          <p className="text-text-muted">
            Latency
          </p>
          <p className="text-text tabular-nums">
            {service.latency_ms}ms
          </p>
        </div>
        <div>
          <p className="text-text-muted">
            Last Checked
          </p>
          <p className="text-text">
            {formatRelativeTime(service.last_checked)}
          </p>
        </div>
      </div>

      {service.message && (
        <p className="mt-3 text-body-sm text-text-secondary">
          {service.message}
        </p>
      )}
    </Card>
  );
}

/**
 * Platform health monitoring page.
 * Shows status of all backend services.
 */
export function HealthPage() {
  const queryClient = useQueryClient();
  const { data: health, isLoading, isFetching } = useAdminHealth();

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'health'] });
  };

  const overallStatus = health?.status ?? 'healthy';

  return (
    <>
      <Header
        title="Platform Health"
        description={health?.checked_at ? `Last checked ${formatRelativeTime(health.checked_at)}` : 'Monitor system status and performance'}
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCw size={16} className={cn(isFetching && 'animate-spin')} />
            {isFetching ? 'Checking...' : 'Refresh'}
          </Button>
        }
      />

      <div className="p-6">
        {/* Overall Status */}
        <Card padding="md" hover={false} className="mb-6">
          <div className="flex items-center gap-4">
            {isLoading ? (
              <>
                <Skeleton className="w-5 h-5 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-5 w-48 mb-1" />
                  <Skeleton className="h-4 w-64" />
                </div>
                <Skeleton className="h-5 w-16" />
              </>
            ) : (
              <>
                {getStatusIcon(overallStatus)}
                <div>
                  <h2 className="text-section-title text-text">
                    Overall System Status
                  </h2>
                  <p className="text-body-sm text-text-secondary">
                    {overallStatus === 'healthy'
                      ? 'All systems operational'
                      : overallStatus === 'degraded'
                        ? 'Some services experiencing issues'
                        : 'Critical services are down'}
                  </p>
                </div>
                <div className="ml-auto">{getStatusBadge(overallStatus)}</div>
              </>
            )}
          </div>
        </Card>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isLoading ? (
            <>
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
            </>
          ) : health?.components && health.components.length > 0 ? (
            health.components.map((service) => (
              <ServiceCard key={service.name} service={service} />
            ))
          ) : (
            <Card padding="md" hover={false} className="col-span-2">
              <p className="text-body-sm text-text-secondary text-center">
                No health data available. Try refreshing.
              </p>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
